// src/pdf-calcs/pdf-calcs-d-rating.js

import {
   primeMoverSingle,
   primeMoverMulti,
   aTrailerBDouble,
   aTrailerBTripleSecond,
   aTrailerBTripleThird,
   dollyHinged,
   dollyHingedSecond,
   dollyRigid,
   dollyRigidSecond,
   dollyDrawbarRigidSingle,
   dollyDrawbarRigidDoubleFirst,
   dollyDrawbarRigidDoubleSecond,
   dollyDrawbarRoadTrainSingle,
   dollyDrawbarRoadTrainDoubleFirst,
   dollyDrawbarRoadTrainDoubleSecond,
} from './d-rating-calculations.js';
import { readNumeric, normalizeValue, formatFieldValue, sumFiniteAxles } from './pdf-calcs-utils.js';

// --- PM Fifth Wheel — Single Trailer ---

const PMS = {
   defaults: {
      pmsPrimeMoverTare: 12.0,
      pmsSteerAxleMass: 7.1,
      pmsDriveAxleMass: 17.5,
      pmsTrailerAxleMass: 23.5,
   },
   rules: {
      pmsPrimeMoverTare: { min: 0, precision: 3, warningMax: 200 },
      pmsSteerAxleMass: { min: 0, precision: 3, warningMax: 200 },
      pmsDriveAxleMass: { min: 0, precision: 3, warningMax: 500 },
      pmsTrailerAxleMass: { min: 0, precision: 3, warningMax: 500 },
   },
};

// --- PM Fifth Wheel — Multi Trailer Combination ---

const PMM = {
   defaults: {
      pmmRearAxleBehind: 70.5,
      pmmSteerAxleMass: 7.1,
      pmmDriveAxleMass: 17.5,
      pmmPrimeMoverTare: 12.0,
   },
   rules: {
      pmmRearAxleBehind: { min: 0, precision: 3, warningMax: 1000 },
      pmmSteerAxleMass: { min: 0, precision: 3, warningMax: 200 },
      pmmDriveAxleMass: { min: 0, precision: 3, warningMax: 500 },
      pmmPrimeMoverTare: { min: 0, precision: 3, warningMax: 200 },
   },
};

// --- A-Trailer Fifth Wheel — B-Double ---

const ATBD = {
   defaults: {
      atbdPrimeMoverTare: 12.0,
      atbdTrailerTare: 6.0,
      atbdRearAxleBehind: 23.5,
      atbdTrailerAxleMass: 23.5,
      atbdTrailerPayload: 20.0,
      atbdFrontAxleAhead: 24.6,
   },
   rules: {
      atbdPrimeMoverTare: { min: 0, precision: 3, warningMax: 200 },
      atbdTrailerTare: { min: 0, precision: 3, warningMax: 200 },
      atbdRearAxleBehind: { min: 0, precision: 3, warningMax: 1000 },
      atbdTrailerAxleMass: { min: 0, precision: 3, warningMax: 500 },
      atbdTrailerPayload: { min: 0, precision: 3, warningMax: 500 },
      atbdFrontAxleAhead: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// --- A-Trailer Fifth Wheel — B-Triple 2nd Trailer ---

const ATBS = {
   defaults: {
      atbsPrimeMoverTare: 12.0,
      atbsTrailerTare: 6.0,
      atbsRearAxleBehind: 47.0,
      atbsTrailerAxleMass: 23.5,
      atbsTrailerPayload: 20.0,
      atbsFrontAxleAhead: 24.6,
   },
   rules: {
      atbsPrimeMoverTare: { min: 0, precision: 3, warningMax: 200 },
      atbsTrailerTare: { min: 0, precision: 3, warningMax: 200 },
      atbsRearAxleBehind: { min: 0, precision: 3, warningMax: 1000 },
      atbsTrailerAxleMass: { min: 0, precision: 3, warningMax: 500 },
      atbsTrailerPayload: { min: 0, precision: 3, warningMax: 500 },
      atbsFrontAxleAhead: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// --- A-Trailer Fifth Wheel — B-Triple 3rd Trailer ---

const ATBT = {
   defaults: {
      atbtPrimeMoverTare: 12.0,
      atbtLeadTrailerTare: 6.0,
      atbtLeadTrailerPayload: 20.0,
      atbtRearAxleBehind: 23.5,
      atbtLeadTrailerAxleMass: 23.5,
      atbtSecondTrailerTare: 6.0,
      atbtSecondTrailerPayload: 20.0,
      atbtSecondTrailerAxleMass: 23.5,
      atbtFrontAxleAhead: 24.6,
   },
   rules: {
      atbtPrimeMoverTare: { min: 0, precision: 3, warningMax: 200 },
      atbtLeadTrailerTare: { min: 0, precision: 3, warningMax: 200 },
      atbtLeadTrailerPayload: { min: 0, precision: 3, warningMax: 500 },
      atbtRearAxleBehind: { min: 0, precision: 3, warningMax: 1000 },
      atbtLeadTrailerAxleMass: { min: 0, precision: 3, warningMax: 500 },
      atbtSecondTrailerTare: { min: 0, precision: 3, warningMax: 200 },
      atbtSecondTrailerPayload: { min: 0, precision: 3, warningMax: 500 },
      atbtSecondTrailerAxleMass: { min: 0, precision: 3, warningMax: 500 },
      atbtFrontAxleAhead: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// --- Dolly Fifth Wheel — Road Train Hinged Drawbar Dolly ---

const DFWH = {
   defaults: {
      dfwhDollyTareMass: 3.5,
      dfwhAxleGroupMassInFrontOfCoupling: 48.1,
      dfwhDollyAxleGroupMass: 23.5,
      dfwhAxleGroupMassBehindCoupling: 70.5,
   },
   rules: {
      dfwhDollyTareMass: { min: 0, precision: 3, warningMax: 200 },
      dfwhAxleGroupMassInFrontOfCoupling: { min: 0, precision: 3, warningMax: 1000 },
      dfwhDollyAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
      dfwhAxleGroupMassBehindCoupling: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// --- Dolly Fifth Wheel — Road Train Hinged Drawbar 2nd Dolly ---

const DFWH2 = {
   defaults: {
      dfwh2DollyTareMass: 3.5,
      dfwh2AxleGroupMassInFrontOfCoupling: 95.1,
      dfwh2DollyAxleGroupMass: 23.5,
      dfwh2AxleGroupMassBehindCoupling: 23.5,
   },
   rules: {
      dfwh2DollyTareMass: { min: 0, precision: 3, warningMax: 200 },
      dfwh2AxleGroupMassInFrontOfCoupling: { min: 0, precision: 3, warningMax: 1000 },
      dfwh2DollyAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
      dfwh2AxleGroupMassBehindCoupling: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// --- Dolly Fifth Wheel — Road Train Rigid Drawbar Dolly ---

const DFWR = {
   defaults: {
      dfwrDollyTareMass: 3.5,
      dfwrAxleGroupMassInFrontOfCoupling: 48.1,
      dfwrDollyAxleGroupMass: 23.5,
      dfwrAxleGroupMassBehindCoupling: 70.5,
      dfwrMassTransferredOntoLeadingTrailerFromDrawbar: 1.5,
   },
   rules: {
      dfwrDollyTareMass: { min: 0, precision: 3, warningMax: 200 },
      dfwrAxleGroupMassInFrontOfCoupling: { min: 0, precision: 3, warningMax: 1000 },
      dfwrDollyAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
      dfwrAxleGroupMassBehindCoupling: { min: 0, precision: 3, warningMax: 1000 },
      dfwrMassTransferredOntoLeadingTrailerFromDrawbar: { min: 0, precision: 3, warningMax: 200 },
   },
};

// --- Dolly Fifth Wheel — Road Train Rigid Drawbar 2nd Dolly ---

const DFWR2 = {
   defaults: {
      dfwr2DollyTareMass: 3.5,
      dfwr2AxleGroupMassInFrontOfCoupling: 95.1,
      dfwr2DollyAxleGroupMass: 23.5,
      dfwr2AxleGroupMassBehindCoupling: 23.5,
      dfwr2MassTransferredOntoLeadingTrailerFromDrawbar: 1.5,
   },
   rules: {
      dfwr2DollyTareMass: { min: 0, precision: 3, warningMax: 200 },
      dfwr2AxleGroupMassInFrontOfCoupling: { min: 0, precision: 3, warningMax: 1000 },
      dfwr2DollyAxleGroupMass: { min: 0, precision: 3, warningMax: 500 },
      dfwr2AxleGroupMassBehindCoupling: { min: 0, precision: 3, warningMax: 1000 },
      dfwr2MassTransferredOntoLeadingTrailerFromDrawbar: { min: 0, precision: 3, warningMax: 200 },
   },
};

// --- Dolly Drawbar Coupling (AS 2213.1:2022) ---

// Rigid Truck & Dog Trailer
const DDRS = {
   defaults: { ddrsFrontMass: 29.5, ddrsRearMass: 47.0 },
   rules: {
      ddrsFrontMass: { min: 0, precision: 3, warningMax: 1000 },
      ddrsRearMass: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// Rigid Truck & 2 Dog Trailers — 1st Drawbar
const DDRD1 = {
   defaults: { ddrd1FrontMass: 29.5, ddrd1RearMass: 94.0 },
   rules: {
      ddrd1FrontMass: { min: 0, precision: 3, warningMax: 1000 },
      ddrd1RearMass: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// Rigid Truck & 2 Dog Trailers — 2nd Drawbar
const DDRD2 = {
   defaults: { ddrd2FrontMass: 76.5, ddrd2RearMass: 47.0 },
   rules: {
      ddrd2FrontMass: { min: 0, precision: 3, warningMax: 1000 },
      ddrd2RearMass: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// Road Train with 1 Dog Trailer
const DDRTS = {
   defaults: { ddrtsFrontMass: 48.1, ddrtsRearMass: 47.0 },
   rules: {
      ddrtsFrontMass: { min: 0, precision: 3, warningMax: 1000 },
      ddrtsRearMass: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// Road Train with 2 Dog Trailers — 1st Drawbar
const DDRTD1 = {
   defaults: { ddrtd1FrontMass: 48.1, ddrtd1RearMass: 94.0 },
   rules: {
      ddrtd1FrontMass: { min: 0, precision: 3, warningMax: 1000 },
      ddrtd1RearMass: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// Road Train with 2 Dog Trailers — 2nd Drawbar
const DDRTD2 = {
   defaults: { ddrtd2FrontMass: 95.1, ddrtd2RearMass: 47.0 },
   rules: {
      ddrtd2FrontMass: { min: 0, precision: 3, warningMax: 1000 },
      ddrtd2RearMass: { min: 0, precision: 3, warningMax: 1000 },
   },
};

// --- Variant registry ────────────────────────────────────────────────────────
// One entry per active calculator variant. To add a new variant:
//   1. Define *_DEFAULTS, *_FIELD_RULES, and a calculate* function below.
//   2. Add a row here — the $(function) loop handles init, events, and
//      blur-normalisation automatically.
//   3. Add the matching panel markup to pdf-calcs-d-rating.ejs.
// ─────────────────────────────────────────────────────────────────────────────

const PM_VARIANTS = [
   {
      key: 'single',
      tabId: 'tab-dr-pm-single',
      containerId: 'dr-pm-variant-single-panel',
      panelId: 'dr-pm-single',
      defaults: PMS.defaults,
      rules: PMS.rules,
      calculate: calculatePmSingle,
   },
   {
      key: 'multi',
      tabId: 'tab-dr-pm-multi',
      containerId: 'dr-pm-variant-multi-panel',
      panelId: 'dr-pm-multi',
      defaults: PMM.defaults,
      rules: PMM.rules,
      calculate: calculatePmMulti,
   },
];

const AT_VARIANTS = [
   {
      key: 'b-double',
      tabId: 'tab-dr-at-b-double',
      containerId: 'dr-at-variant-b-double-panel',
      panelId: 'dr-at-b-double',
      defaults: ATBD.defaults,
      rules: ATBD.rules,
      calculate: calculateAtBDouble,
   },
   {
      key: 'btriple-second',
      tabId: 'tab-dr-at-btriple-second',
      containerId: 'dr-at-variant-btriple-second-panel',
      panelId: 'dr-at-btriple-second',
      defaults: ATBS.defaults,
      rules: ATBS.rules,
      calculate: calculateAtBTripleSecond,
   },
   {
      key: 'btriple-third',
      tabId: 'tab-dr-at-btriple-third',
      containerId: 'dr-at-variant-btriple-third-panel',
      panelId: 'dr-at-btriple-third',
      defaults: ATBT.defaults,
      rules: ATBT.rules,
      calculate: calculateAtBTripleThird,
   },
];

const DOLLY_VARIANTS = [
   {
      key: 'dollyFifthWheelHingedDrawbar',
      tabId: 'tab-dr-dolly-hinged',
      containerId: 'dr-dolly-variant-hinged-panel',
      panelId: 'dr-dolly-hinged',
      defaults: DFWH.defaults,
      rules: DFWH.rules,
      calculate: calculateDollyHinged,
   },
   {
      key: 'dollyFifthWheelHingedDrawbarSecond',
      tabId: 'tab-dr-dolly-hinged-second',
      containerId: 'dr-dolly-variant-hinged-second-panel',
      panelId: 'dr-dolly-hinged-second',
      defaults: DFWH2.defaults,
      rules: DFWH2.rules,
      calculate: calculateDollyHingedSecond,
   },
   {
      key: 'dollyFifthWheelRigidDrawbar',
      tabId: 'tab-dr-dolly-rigid',
      containerId: 'dr-dolly-variant-rigid-panel',
      panelId: 'dr-dolly-rigid',
      defaults: DFWR.defaults,
      rules: DFWR.rules,
      calculate: calculateDollyRigid,
   },
   {
      key: 'dollyFifthWheelRigidDrawbarSecond',
      tabId: 'tab-dr-dolly-rigid-second',
      containerId: 'dr-dolly-variant-rigid-second-panel',
      panelId: 'dr-dolly-rigid-second',
      defaults: DFWR2.defaults,
      rules: DFWR2.rules,
      calculate: calculateDollyRigidSecond,
   },
];

const DRAWBAR_VARIANTS = [
   {
      key: 'rigid-single',
      tabId: 'tab-dr-drawbar-rigid-single',
      containerId: 'dr-drawbar-variant-rigid-single-panel',
      panelId: 'dr-drawbar-rigid-single',
      defaults: DDRS.defaults,
      rules: DDRS.rules,
      calculate: calculateDrawbarRigidSingle,
   },
   {
      key: 'rigid-double-first',
      tabId: 'tab-dr-drawbar-rigid-double-first',
      containerId: 'dr-drawbar-variant-rigid-double-first-panel',
      panelId: 'dr-drawbar-rigid-double-first',
      defaults: DDRD1.defaults,
      rules: DDRD1.rules,
      calculate: calculateDrawbarRigidDoubleFirst,
   },
   {
      key: 'rigid-double-second',
      tabId: 'tab-dr-drawbar-rigid-double-second',
      containerId: 'dr-drawbar-variant-rigid-double-second-panel',
      panelId: 'dr-drawbar-rigid-double-second',
      defaults: DDRD2.defaults,
      rules: DDRD2.rules,
      calculate: calculateDrawbarRigidDoubleSecond,
   },
   {
      key: 'roadtrain-single',
      tabId: 'tab-dr-drawbar-roadtrain-single',
      containerId: 'dr-drawbar-variant-roadtrain-single-panel',
      panelId: 'dr-drawbar-roadtrain-single',
      defaults: DDRTS.defaults,
      rules: DDRTS.rules,
      calculate: calculateDrawbarRoadTrainSingle,
   },
   {
      key: 'roadtrain-double-first',
      tabId: 'tab-dr-drawbar-roadtrain-double-first',
      containerId: 'dr-drawbar-variant-roadtrain-double-first-panel',
      panelId: 'dr-drawbar-roadtrain-double-first',
      defaults: DDRTD1.defaults,
      rules: DDRTD1.rules,
      calculate: calculateDrawbarRoadTrainDoubleFirst,
   },
   {
      key: 'roadtrain-double-second',
      tabId: 'tab-dr-drawbar-roadtrain-double-second',
      containerId: 'dr-drawbar-variant-roadtrain-double-second-panel',
      panelId: 'dr-drawbar-roadtrain-double-second',
      defaults: DDRTD2.defaults,
      rules: DDRTD2.rules,
      calculate: calculateDrawbarRoadTrainDoubleSecond,
   },
];

// --- Page load and event handling ---

$(function () {
   const $page = $('#pdf-calcs-content');
   if ($page.length === 0) return;

   const variantGroups = [
      initVariantGroup($page, PM_VARIANTS, '#dr-pm-variant-tabs', 'data-variant', '#dr-pm-fw'),
      initVariantGroup($page, AT_VARIANTS, '#dr-at-variant-tabs', 'data-at-variant', '#dr-at-fw'),
      initVariantGroup($page, DOLLY_VARIANTS, '#dr-dolly-variant-tabs', 'data-dolly-variant', '#dr-dolly-fw'),
      initVariantGroup($page, DRAWBAR_VARIANTS, '#dr-drawbar-variant-tabs', 'data-drawbar-variant', '#dr-drawbar'),
   ];

   const $resetButton = $page.find('#dr-reset-defaults');

   function getActiveGroup() {
      return variantGroups.find((group) => group.isCategoryActive()) || null;
   }

   function updateResetButtonState() {
      $resetButton.prop('disabled', getActiveGroup() == null);
   }

   $resetButton.on('click', function () {
      const activeGroup = getActiveGroup();
      if (!activeGroup) return;
      activeGroup.resetActiveVariant();
   });

   $page.on('shown.bs.tab', '#dr-category-tabs [data-bs-toggle="tab"]', updateResetButtonState);

   updateResetButtonState();
});

function initVariantGroup($page, variants, tabsSelector, variantAttr, categoryPaneSelector) {
   const byKey = new Map(variants.map((v) => [v.key, v]));

   function applyVariantDefaults(variant) {
      const $panel = $page.find(`#${variant.panelId}`);
      for (const [fieldId, value] of Object.entries(variant.defaults)) {
         $panel.find(`[data-field="${fieldId}"]`).val(value);
      }
   }

   function getActiveVariant() {
      for (const variant of variants) {
         if (!$page.find(`#${variant.containerId}`).hasClass('d-none')) {
            return variant;
         }
      }
      return variants[0];
   }

   function isActiveVariant(variant) {
      return getActiveVariant().key === variant.key;
   }

   function setActiveVariant(key) {
      const active = byKey.get(key);
      if (!active) return;

      for (const variant of variants) {
         const isActive = variant.key === active.key;
         $page.find(`#${variant.containerId}`).toggleClass('d-none', !isActive);
      }

      // Keep select in sync (no-op if the change event triggered this)
      $page.find(tabsSelector).filter('select').val(key);

      active.calculate($page);
   }

   for (const variant of variants) {
      const $panel = $page.find(`#${variant.panelId}`);

      applyVariantDefaults(variant);

      $panel.on('input change', '[data-field]', function () {
         if (!isActiveVariant(variant)) return;
         variant.calculate($page);
      });

      $panel.on('blur', '[data-field]', function () {
         if (!isActiveVariant(variant)) return;
         const fieldId = $(this).data('field');
         const rule = variant.rules[fieldId];
         if (!rule) return;
         const val = normalizeValue(readNumeric($(this).val()), rule);
         $(this).val(formatFieldValue(val, rule));
         variant.calculate($page);
      });
   }

   const $selectorEl = $page.find(tabsSelector);
   if ($selectorEl.is('select')) {
      $selectorEl.on('change', function () {
         setActiveVariant($(this).val());
      });
   } else {
      $page.on('click', `${tabsSelector} [${variantAttr}]`, function () {
         setActiveVariant($(this).attr(variantAttr));
      });
   }

   setActiveVariant(getActiveVariant().key);

   return {
      isCategoryActive() {
         const $categoryPane = $page.find(categoryPaneSelector);
         return $categoryPane.hasClass('active') || $categoryPane.hasClass('show');
      },
      resetActiveVariant() {
         const activeVariant = getActiveVariant();
         applyVariantDefaults(activeVariant);
         activeVariant.calculate($page);
      },
   };
}

// --- PM Fifth Wheel — Single Trailer: calculation and UI update ---

function calculatePmSingle($page) {
   const $panel = $page.find('#dr-pm-single');
   const steerAxleMass = readNumeric($panel.find('[data-field="pmsSteerAxleMass"]').val());
   const driveAxleMass = readNumeric($panel.find('[data-field="pmsDriveAxleMass"]').val());
   const trailerAxleMass = readNumeric($panel.find('[data-field="pmsTrailerAxleMass"]').val());
   const totalCombinationMass = sumFiniteAxles(steerAxleMass, driveAxleMass, trailerAxleMass);

   $panel
      .find('#pmsTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const inputs = {
      primeMoverTare: readNumeric($panel.find('[data-field="pmsPrimeMoverTare"]').val()),
      steerAxleMass,
      driveAxleMass,
      trailerAxleMass,
   };

   const result = primeMoverSingle(inputs);
   const $display = $panel.find('#result-pms-d');

   if (result.ok) {
      $display.text(result.roundedD);
   } else {
      $display.text('—');
   }
}

// --- PM Fifth Wheel — Multi Trailer: calculation and UI update ---

function calculatePmMulti($page) {
   const $panel = $page.find('#dr-pm-multi');
   const steerAxleMass = readNumeric($panel.find('[data-field="pmmSteerAxleMass"]').val());
   const driveAxleMass = readNumeric($panel.find('[data-field="pmmDriveAxleMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="pmmRearAxleBehind"]').val());
   const totalCombinationMass = sumFiniteAxles(steerAxleMass, driveAxleMass, rearAxleBehind);

   $panel
      .find('#pmmTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '\u2014');

   const inputs = {
      totalCombinationMass,
      rearAxleBehind,
      steerAxleMass,
      driveAxleMass,
      primeMoverTare: readNumeric($panel.find('[data-field="pmmPrimeMoverTare"]').val()),
   };

   const result = primeMoverMulti(inputs);
   const $display = $panel.find('#result-pmm-d');

   if (result.ok) {
      $display.text(result.roundedD);
   } else {
      $display.text('—');
   }
}

// --- A-Trailer Fifth Wheel — B-Double ---

function calculateAtBDouble($page) {
   const $panel = $page.find('#dr-at-b-double');
   const frontAxleAhead = readNumeric($panel.find('[data-field="atbdFrontAxleAhead"]').val());
   const trailerAxleMass = readNumeric($panel.find('[data-field="atbdTrailerAxleMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="atbdRearAxleBehind"]').val());
   const totalCombinationMass = sumFiniteAxles(frontAxleAhead, trailerAxleMass, rearAxleBehind);

   $panel
      .find('#atbdTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '\u2014');

   const inputs = {
      totalCombinationMass,
      primeMoverTare: readNumeric($panel.find('[data-field="atbdPrimeMoverTare"]').val()),
      trailerTare: readNumeric($panel.find('[data-field="atbdTrailerTare"]').val()),
      rearAxleBehind,
      trailerAxleMass,
      trailerPayload: readNumeric($panel.find('[data-field="atbdTrailerPayload"]').val()),
      frontAxleAhead,
   };

   const result = aTrailerBDouble(inputs);
   const $display = $panel.find('#result-atbd-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- A-Trailer Fifth Wheel — B-Triple 2nd Trailer ---

function calculateAtBTripleSecond($page) {
   const $panel = $page.find('#dr-at-btriple-second');
   const frontAxleAhead = readNumeric($panel.find('[data-field="atbsFrontAxleAhead"]').val());
   const trailerAxleMass = readNumeric($panel.find('[data-field="atbsTrailerAxleMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="atbsRearAxleBehind"]').val());
   const totalCombinationMass = sumFiniteAxles(frontAxleAhead, trailerAxleMass, rearAxleBehind);

   $panel
      .find('#atbsTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '\u2014');

   const inputs = {
      totalCombinationMass,
      primeMoverTare: readNumeric($panel.find('[data-field="atbsPrimeMoverTare"]').val()),
      trailerTare: readNumeric($panel.find('[data-field="atbsTrailerTare"]').val()),
      rearAxleBehind,
      trailerAxleMass,
      trailerPayload: readNumeric($panel.find('[data-field="atbsTrailerPayload"]').val()),
      frontAxleAhead,
   };

   const result = aTrailerBTripleSecond(inputs);
   const $display = $panel.find('#result-atbs-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- A-Trailer Fifth Wheel — B-Triple 3rd Trailer ---

function calculateAtBTripleThird($page) {
   const $panel = $page.find('#dr-at-btriple-third');
   const frontAxleAhead = readNumeric($panel.find('[data-field="atbtFrontAxleAhead"]').val());
   const leadTrailerAxleMass = readNumeric($panel.find('[data-field="atbtLeadTrailerAxleMass"]').val());
   const secondTrailerAxleMass = readNumeric($panel.find('[data-field="atbtSecondTrailerAxleMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="atbtRearAxleBehind"]').val());
   const totalCombinationMass = sumFiniteAxles(
      frontAxleAhead,
      leadTrailerAxleMass,
      secondTrailerAxleMass,
      rearAxleBehind,
   );

   $panel
      .find('#atbtTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const inputs = {
      totalCombinationMass,
      primeMoverTare: readNumeric($panel.find('[data-field="atbtPrimeMoverTare"]').val()),
      leadTrailerTare: readNumeric($panel.find('[data-field="atbtLeadTrailerTare"]').val()),
      leadTrailerPayload: readNumeric($panel.find('[data-field="atbtLeadTrailerPayload"]').val()),
      rearAxleBehind,
      leadTrailerAxleMass,
      secondTrailerTare: readNumeric($panel.find('[data-field="atbtSecondTrailerTare"]').val()),
      secondTrailerPayload: readNumeric($panel.find('[data-field="atbtSecondTrailerPayload"]').val()),
      secondTrailerAxleMass,
      frontAxleAhead,
   };

   const result = aTrailerBTripleThird(inputs);
   const $display = $panel.find('#result-atbt-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Fifth Wheel — Road Train Hinged Drawbar Dolly ---

function calculateDollyHinged($page) {
   const $panel = $page.find('#dr-dolly-hinged');
   const frontAxleAhead = readNumeric($panel.find('[data-field="dfwhAxleGroupMassInFrontOfCoupling"]').val());
   const dollyAxleMass = readNumeric($panel.find('[data-field="dfwhDollyAxleGroupMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="dfwhAxleGroupMassBehindCoupling"]').val());
   const totalCombinationMass = sumFiniteAxles(frontAxleAhead, dollyAxleMass, rearAxleBehind);

   $panel
      .find('#dfwhTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '\u2014');

   const inputs = {
      totalCombinationMass,
      dollyTare: readNumeric($panel.find('[data-field="dfwhDollyTareMass"]').val()),
      frontAxleAhead,
      dollyAxleMass,
      rearAxleBehind,
   };

   const result = dollyHinged(inputs);
   const $display = $panel.find('#result-dfwh-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Fifth Wheel — Road Train Hinged Drawbar 2nd Dolly ---

function calculateDollyHingedSecond($page) {
   const $panel = $page.find('#dr-dolly-hinged-second');
   const frontAxleAhead = readNumeric($panel.find('[data-field="dfwh2AxleGroupMassInFrontOfCoupling"]').val());
   const dollyAxleMass = readNumeric($panel.find('[data-field="dfwh2DollyAxleGroupMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="dfwh2AxleGroupMassBehindCoupling"]').val());
   const totalCombinationMass = sumFiniteAxles(frontAxleAhead, dollyAxleMass, rearAxleBehind);

   $panel
      .find('#dfwh2TotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '\u2014');

   const inputs = {
      totalCombinationMass,
      dollyTare: readNumeric($panel.find('[data-field="dfwh2DollyTareMass"]').val()),
      frontAxleAhead,
      dollyAxleMass,
      rearAxleBehind,
   };

   const result = dollyHingedSecond(inputs);
   const $display = $panel.find('#result-dfwh2-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Fifth Wheel — Road Train Rigid Drawbar Dolly ---

function calculateDollyRigid($page) {
   const $panel = $page.find('#dr-dolly-rigid');
   const frontAxleAhead = readNumeric($panel.find('[data-field="dfwrAxleGroupMassInFrontOfCoupling"]').val());
   const dollyAxleMass = readNumeric($panel.find('[data-field="dfwrDollyAxleGroupMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="dfwrAxleGroupMassBehindCoupling"]').val());
   const totalCombinationMass = sumFiniteAxles(frontAxleAhead, dollyAxleMass, rearAxleBehind);

   $panel
      .find('#dfwrTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '\u2014');

   const inputs = {
      totalCombinationMass,
      dollyTare: readNumeric($panel.find('[data-field="dfwrDollyTareMass"]').val()),
      frontAxleAhead,
      dollyAxleMass,
      rearAxleBehind,
      transferMass: readNumeric($panel.find('[data-field="dfwrMassTransferredOntoLeadingTrailerFromDrawbar"]').val()),
   };

   const result = dollyRigid(inputs);
   const $display = $panel.find('#result-dfwr-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Fifth Wheel — Road Train Rigid Drawbar 2nd Dolly ---

function calculateDollyRigidSecond($page) {
   const $panel = $page.find('#dr-dolly-rigid-second');
   const frontAxleAhead = readNumeric($panel.find('[data-field="dfwr2AxleGroupMassInFrontOfCoupling"]').val());
   const dollyAxleMass = readNumeric($panel.find('[data-field="dfwr2DollyAxleGroupMass"]').val());
   const rearAxleBehind = readNumeric($panel.find('[data-field="dfwr2AxleGroupMassBehindCoupling"]').val());
   const totalCombinationMass = sumFiniteAxles(frontAxleAhead, dollyAxleMass, rearAxleBehind);

   $panel
      .find('#dfwr2TotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '\u2014');

   const inputs = {
      totalCombinationMass,
      dollyTare: readNumeric($panel.find('[data-field="dfwr2DollyTareMass"]').val()),
      frontAxleAhead,
      dollyAxleMass,
      rearAxleBehind,
      transferMass: readNumeric($panel.find('[data-field="dfwr2MassTransferredOntoLeadingTrailerFromDrawbar"]').val()),
   };

   const result = dollyRigidSecond(inputs);
   const $display = $panel.find('#result-dfwr2-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Drawbar Coupling — Rigid Truck & Dog Trailer ---

function calculateDrawbarRigidSingle($page) {
   const $panel = $page.find('#dr-drawbar-rigid-single');
   const frontMass = readNumeric($panel.find('[data-field="ddrsFrontMass"]').val());
   const rearMass = readNumeric($panel.find('[data-field="ddrsRearMass"]').val());
   const totalCombinationMass = sumFiniteAxles(frontMass, rearMass);

   $panel
      .find('#ddrsTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const result = dollyDrawbarRigidSingle({ frontMass, rearMass });
   const $display = $panel.find('#result-ddrs-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Drawbar Coupling — Rigid Truck & 2 Dog Trailers (1st Drawbar) ---

function calculateDrawbarRigidDoubleFirst($page) {
   const $panel = $page.find('#dr-drawbar-rigid-double-first');
   const frontMass = readNumeric($panel.find('[data-field="ddrd1FrontMass"]').val());
   const rearMass = readNumeric($panel.find('[data-field="ddrd1RearMass"]').val());
   const totalCombinationMass = sumFiniteAxles(frontMass, rearMass);

   $panel
      .find('#ddrd1TotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const result = dollyDrawbarRigidDoubleFirst({ frontMass, rearMass });
   const $display = $panel.find('#result-ddrd1-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Drawbar Coupling — Rigid Truck & 2 Dog Trailers (2nd Drawbar) ---

function calculateDrawbarRigidDoubleSecond($page) {
   const $panel = $page.find('#dr-drawbar-rigid-double-second');
   const frontMass = readNumeric($panel.find('[data-field="ddrd2FrontMass"]').val());
   const rearMass = readNumeric($panel.find('[data-field="ddrd2RearMass"]').val());
   const totalCombinationMass = sumFiniteAxles(frontMass, rearMass);

   $panel
      .find('#ddrd2TotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const result = dollyDrawbarRigidDoubleSecond({ frontMass, rearMass });
   const $display = $panel.find('#result-ddrd2-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Drawbar Coupling — Road Train with 1 Dog Trailer ---

function calculateDrawbarRoadTrainSingle($page) {
   const $panel = $page.find('#dr-drawbar-roadtrain-single');
   const frontMass = readNumeric($panel.find('[data-field="ddrtsFrontMass"]').val());
   const rearMass = readNumeric($panel.find('[data-field="ddrtsRearMass"]').val());
   const totalCombinationMass = sumFiniteAxles(frontMass, rearMass);

   $panel
      .find('#ddrtsTotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const result = dollyDrawbarRoadTrainSingle({ frontMass, rearMass });
   const $display = $panel.find('#result-ddrts-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Drawbar Coupling — Road Train with 2 Dog Trailers (1st Drawbar) ---

function calculateDrawbarRoadTrainDoubleFirst($page) {
   const $panel = $page.find('#dr-drawbar-roadtrain-double-first');
   const frontMass = readNumeric($panel.find('[data-field="ddrtd1FrontMass"]').val());
   const rearMass = readNumeric($panel.find('[data-field="ddrtd1RearMass"]').val());
   const totalCombinationMass = sumFiniteAxles(frontMass, rearMass);

   $panel
      .find('#ddrtd1TotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const result = dollyDrawbarRoadTrainDoubleFirst({ frontMass, rearMass });
   const $display = $panel.find('#result-ddrtd1-d');
   $display.text(result.ok ? result.roundedD : '—');
}

// --- Dolly Drawbar Coupling — Road Train with 2 Dog Trailers (2nd Drawbar) ---

function calculateDrawbarRoadTrainDoubleSecond($page) {
   const $panel = $page.find('#dr-drawbar-roadtrain-double-second');
   const frontMass = readNumeric($panel.find('[data-field="ddrtd2FrontMass"]').val());
   const rearMass = readNumeric($panel.find('[data-field="ddrtd2RearMass"]').val());
   const totalCombinationMass = sumFiniteAxles(frontMass, rearMass);

   $panel
      .find('#ddrtd2TotalCombinationMass')
      .text(Number.isFinite(totalCombinationMass) ? parseFloat(totalCombinationMass.toFixed(3)).toString() : '—');

   const result = dollyDrawbarRoadTrainDoubleSecond({ frontMass, rearMass });
   const $display = $panel.find('#result-ddrtd2-d');
   $display.text(result.ok ? result.roundedD : '—');
}
